import '@src/components/TefComsCancelButton.js';
import '@src/components/TefComsButton.js';
import '@src/components/TefComsBackButton.js';

import {
  html, nothing, css, unsafeCSS,
} from 'lit';
import {
  customElement, property,
  state,
} from 'lit/decorators.js';
import { Api4pClient } from '@common/clients/Api4pClient.js';
import { StandardMicrofrontendMixin } from '@common/lit/mixins/StandardMicrofrontendMixin.js';
import type {
  App, ConsentUpdate, Purpose, Subscription,
} from '@common/types/model.d.js';
import { assert } from '@common/utils/assert.js';
import { BaseComponent } from '@src/components/BaseComponent.js';
import styles from '@src/components/TefComs4p.css' with { type: 'text' };
import { config, DisplayMode } from '@src/config/index.js';
import { componentStateStore, configStore, consentStore } from '@src/store/index.js';
import { diffPurposesAsPayload } from '@src/util/diffPurposeAsPayload.js';

declare const window: {
  one?: { // tef-one
    ready?: unknown;
  };
} & Window;

@customElement('tef-coms4p')
export class TefComs4p extends StandardMicrofrontendMixin(BaseComponent, configStore, config) {
  // STATIC style imported above (for dynamic values use --css-vars)
  static readonly styles = css`${unsafeCSS(styles)}`;

  // These properties are controllable from the outside via HTML attributes eg. on the components tag
  @property({ type: String, attribute: 'api-url' }) apiUrl! : string;
  @property({ type: String, attribute: 'fetch' }) fetch?: string;
  @property({ type: String, attribute: 'lc-id' }) lcId?: string;
  @property({ type: String, attribute: 'sp-id' }) spId?: string;
  @property({ type: String, attribute: 'mode' }) mode?: string;

  // The add has a "view" and "edit" mode
  @state() displayMode : DisplayMode = DisplayMode.view;

  client: Api4pClient | undefined;
  #isInitDone : boolean = false;

  constructor() {
    super();
    this.setupStore(consentStore);
    this.setupStore(componentStateStore);
  }

  // Called when the DOM has been updated (See https://lit.dev/docs/components/lifecycle/#updated)
  override updated(changedProperties: Map<PropertyKey, unknown>) {
    this.updateConfigStoreValues(changedProperties);

    // The component is usable when uncloaked
    if (!this.#isInitDone && !this.cloak) {
      this.triggerEvent({ name: 'load/READY', level: 'info' });
      this.#isInitDone = true;
    }
  }

  override render() {
    if (consentStore.actualConsents.length > 0) {
      return this.getTemplateFn(`tefComs4p${componentStateStore.displayMode}`)();
    }
    return html`${nothing}`;
  }

  // Invoked when a component is added to the document's DOM.
  override async connectedCallback() {
    try {
      // Required attributes must be present on the element:
      assert(!!this.apiUrl, 'missing required html attribute "api-url"');
      assert(
        !!this.themeBaseUrl || !!window.one?.ready,
        'theme-base-url is required if OCCL design system is used',
      );

      // Call super only AFTER validating all required attributes!
      super.connectedCallback();
      await this.setup();
    } catch (error) {
      this.triggerEvent({ name: 'load/ERROR', level: 'error', error: error as Error });
      this.#isInitDone = true;
      throw error;
    }
  }

  // General comp setup (loading data etc and handling errors loading that data)
  async setup() {
    // Load consents
    this.client = new Api4pClient({
      apiUrl: this.apiUrl, accessToken: this.accessToken, altFetch: this.fetch, traceid: this.traceid,
    });

    try {
      consentStore.initialConsents = await this.client.getConsents(this.spId, this.lcId);
    } catch (error : unknown) {
      this.triggerEvent({ name: 'consents/ERROR', level: 'error', error: error as Error });
      throw error;
    }

    // Copy loaded consents as working copy
    consentStore.actualConsents = structuredClone(consentStore.initialConsents);

    if (consentStore.initialConsents.length === 0) {
      this.triggerEvent({ name: 'consents/EMPTY', level: 'info' });
    } else {
      this.triggerEvent({ name: 'consents/LOAD', level: 'info' });
    }
  }

  editPurpose(purposeId : string) {
    // Sanity check
    const activePurpose = consentStore.findPurposeById(purposeId);
    if (!activePurpose) {
      return;
    }

    // Switch to EDIT mode for that purpose
    componentStateStore.activePurpose = activePurpose;
    componentStateStore.displayMode = DisplayMode.edit;

    this.triggerEvent({ name: 'action/CHANGE', level: 'info' });
  }

  // Sets a consent for an purpose -> subscription -> app
  toggleAppConsent(event: PointerEvent, app: App) {
    // Since app is still a reference to actualConsents we can just toggle it, nice.
    app.granted = !app.granted; // eslint-disable-line no-param-reassign

    // The event is triggered twice so cancel it here
    event.preventDefault();

    this.triggerEvent({ name: 'consents/CHANGE', level: 'info' });
    this.requestUpdate();
  }

  revokeAllAppConsentsForSubscription(subscription : Subscription) {
    // Subscription is a reference to actualConsents
    subscription.apps.forEach((app) => {
      app.granted = false; // eslint-disable-line no-param-reassign
    });

    this.triggerEvent({ name: 'consents/CHANGE', level: 'info' });
    this.requestUpdate();
  }

  revokeAllAppConsentsForPurpose(purpose: Purpose) {
    if (!Array.isArray(purpose.apps)) {
      throw new Error('[TefComs4p] Called revokeAllAppConsentsForPurpose without valid purpose.apps');
    }
    purpose.apps.forEach((app : App) => {
      app.granted = false; // eslint-disable-line no-param-reassign
    });

    this.triggerEvent({ name: 'consents/CHANGE', level: 'info' });
    this.requestUpdate();
  }

  // Saves the current consent setting for the active purpose
  async saveConsentsForActivePurpose() {
    // Sanity check
    if (!componentStateStore.activePurpose) {
      return;
    }

    this.triggerEvent({ name: 'action/SAVE', level: 'info' });

    // Only send the differences between the initial and actual purpose
    /// so that we don't accidently update some timestamp in the DB
    const payload = this.getChangedConsentsAsPayload();

    if (payload.length === 0) return;

    const { tenant } = componentStateStore.activePurpose;

    try {
      await this.client!.postConsentsUpdateList(tenant, payload, this.spId, this.lcId);

      // Success
      this.triggerEvent({ name: 'consents/SAVE', level: 'info' });

      // Reload data to reflect actual result
      await this.setup();
    } catch (error) {
      this.triggerEvent({ name: 'consents/ERROR', level: 'error', error: error as Error });
      throw error;
    }
  }

  getChangedConsentsAsPayload() : ConsentUpdate[] {
    const { activePurpose } = componentStateStore;
    const initialPurpose = consentStore.initialConsents.find((purpose) => purpose.purposeId === activePurpose.purposeId);
    if (!initialPurpose) return [];

    return diffPurposesAsPayload(initialPurpose, activePurpose);
  }
}
